import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { taskService } from "../services/task.service";
import type {
	AddTaskLocationsRequest,
	AddTaskPlantsRequest,
	CompleteTaskItemRequest,
	CreateTaskRequest,
	ErrorResponse,
	ListTasksParams,
	UpdateTaskRequest,
	UpdateTaskStatusRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const taskKeys = {
	all: ["tasks"] as const,
	lists: () => [...taskKeys.all, "list"] as const,
	list: (params?: ListTasksParams) => [...taskKeys.lists(), params] as const,
	details: () => [...taskKeys.all, "detail"] as const,
	detail: (id: string) => [...taskKeys.details(), id] as const,
};

// ─── useTasks (list) ──────────────────────────────────────────────────
export function useTasks(params?: ListTasksParams) {
	return useQuery({
		queryKey: taskKeys.list(params),
		queryFn: () => taskService.list(params),
	});
}

// ─── useTask (getById) ────────────────────────────────────────────────
export function useTask(id: string) {
	return useQuery({
		queryKey: taskKeys.detail(id),
		queryFn: () => taskService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateTask ───────────────────────────────────────────────────
export function useCreateTask() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateTaskRequest) => taskService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
			message.success("Topshiriq muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Topshiriq yaratishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateTask ───────────────────────────────────────────────────
export function useUpdateTask() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateTaskRequest }) =>
			taskService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Topshiriq muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Topshiriqni yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteTask ───────────────────────────────────────────────────
export function useDeleteTask() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => taskService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
			message.success("Topshiriq muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Topshiriqni o'chirishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateTaskStatus ─────────────────────────────────────────────
export function useUpdateTaskStatus() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateTaskStatusRequest }) =>
			taskService.updateStatus(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Topshiriq statusi yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Topshiriq statusini yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useAddTaskLocations ─────────────────────────────────────────────
export function useAddTaskLocations() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: AddTaskLocationsRequest }) =>
			taskService.addLocations(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Joylashuv qo'shildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Joylashuv qo'shishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useRemoveTaskLocation ────────────────────────────────────────────
export function useRemoveTaskLocation() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, locationId }: { id: string; locationId: string }) =>
			taskService.removeLocation(id, locationId),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Joylashuv olib tashlandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Joylashuvni olib tashlashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useAddTaskPlants ─────────────────────────────────────────────────
export function useAddTaskPlants() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: AddTaskPlantsRequest }) =>
			taskService.addPlants(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Ekin qo'shildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ekin qo'shishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useRemoveTaskPlant ───────────────────────────────────────────────
export function useRemoveTaskPlant() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, plantId }: { id: string; plantId: string }) =>
			taskService.removePlant(id, plantId),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Ekin olib tashlandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ekinni olib tashlashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useCompleteTaskLocation ──────────────────────────────────────────
export function useCompleteTaskLocation() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			locationId,
			data,
		}: {
			id: string;
			locationId: string;
			data: CompleteTaskItemRequest;
		}) => taskService.completeLocation(id, locationId, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Joylashuv bajarildi deb belgilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Joylashuvni belgilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useCompleteTaskPlant ─────────────────────────────────────────────
export function useCompleteTaskPlant() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			plantId,
			data,
		}: {
			id: string;
			plantId: string;
			data: CompleteTaskItemRequest;
		}) => taskService.completePlant(id, plantId, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
			message.success("Ekin bajarildi deb belgilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ekinni belgilashda xatolik";
			message.error(msg);
		},
	});
}
